/**
 * The job form (WT-2, fieldiq-api#1245): a full page for New job, an inline
 * panel on the job's page for Edit. Never a modal — a job is a record.
 *
 * THE WELL DECIDES THE CUSTOMER. The form picks a well and never sends
 * `customer_id`; the API takes the well's customer, so well_customer_mismatch
 * cannot arise from here. The customer is shown read-only beside the pick.
 *
 * Load to recover is sent as typed with its unit (the tenant's liquid-volume
 * unit by default); the API keeps that and a metric copy. A blank is sent as
 * null, never 0. On edit only the changed keys are sent. Status is not edited
 * here: a new job starts as Draft or Scheduled, and moves by the status
 * actions afterwards.
 */
import React, { useMemo, useState } from 'react';
import { useWtWells } from '../../hooks/useWellTesting';
import { blankToNull, wellIdOf } from '../../lib/well-testing';
import type { WtCatalog, WtJob, WtJobWrite, WtMeta } from '../../types/well-testing';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError } from './WtBits';

export interface JobFormState {
  well_id: string;
  ticket_mode: string;
  supervisor_id: string;
  planned_start_on: string;
  planned_end_on: string;
  scope: string;
  po_number: string;
  afe_number: string;
  cost_code: string;
  flare_permit_ref: string;
  load_to_recover: string;
  load_to_recover_unit: string;
  notes: string;
  status: string;
}

export const jobFormFrom = (j: WtJob | null, defaults: { wellId?: string; liquidUnit?: string | null } = {}): JobFormState => ({
  well_id: j?.wellId ?? defaults.wellId ?? '',
  ticket_mode: j?.ticketMode ?? '',
  supervisor_id: j?.supervisorId ?? '',
  planned_start_on: j?.plannedStartOn ?? '',
  planned_end_on: j?.plannedEndOn ?? '',
  scope: j?.scope ?? '',
  po_number: j?.poNumber ?? '',
  afe_number: j?.afeNumber ?? '',
  cost_code: j?.costCode ?? '',
  flare_permit_ref: j?.flarePermitRef ?? '',
  load_to_recover: j?.loadToRecover?.entered?.value ?? '',
  load_to_recover_unit: j?.loadToRecover?.entered?.unit ?? defaults.liquidUnit ?? '',
  notes: j?.notes ?? '',
  status: 'draft',
});

const TEXT_KEYS = [
  'supervisor_id', 'planned_start_on', 'planned_end_on', 'scope', 'po_number', 'afe_number', 'cost_code', 'flare_permit_ref', 'notes',
] as const;

/** The write body: every field on create, the changed ones on edit. Exported for the test. */
export function jobWriteBody(form: JobFormState, original: JobFormState | null): WtJobWrite {
  const out: Record<string, unknown> = {};
  const changed = (k: keyof JobFormState) => !original || form[k] !== original[k];
  if (changed('well_id')) out.well_id = form.well_id;
  if (changed('ticket_mode') && form.ticket_mode) out.ticket_mode = form.ticket_mode;
  for (const k of TEXT_KEYS)
    if (changed(k)) {
      const v = blankToNull(form[k]);
      if (original || v !== null) out[k] = v;
    }
  if (changed('load_to_recover') || changed('load_to_recover_unit')) {
    const v = blankToNull(form.load_to_recover);
    if (original || v !== null) {
      out.load_to_recover = v;
      if (v !== null && form.load_to_recover_unit) out.load_to_recover_unit = form.load_to_recover_unit;
    }
  }
  if (!original) out.status = form.status;
  return out as WtJobWrite;
}

export function JobForm({
  job,
  initialWellId,
  catalog,
  meta,
  users,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  job: WtJob | null;
  initialWellId?: string;
  catalog: WtCatalog | null;
  meta: WtMeta | null;
  users: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WtJobWrite) => void;
}) {
  const liquidUnit = meta?.units?.liquidVolume ?? null;
  const original = useMemo(() => (job ? jobFormFrom(job) : null), [job]);
  const [form, setForm] = useState<JobFormState>(() => jobFormFrom(job, { wellId: initialWellId, liquidUnit }));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = (k: keyof JobFormState, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const wells = useWtWells();
  const wellList = wells.data?.data ?? [];
  const tenant = wells.data?.meta ?? meta;
  const picked = wellList.find((w) => w.id === form.well_id) ?? null;
  const liquidUnits = catalog?.quantityUnits?.liquid_volume?.accepted ?? (liquidUnit ? [liquidUnit] : []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.well_id) return setLocalError('Choose the well.');
    if (form.planned_start_on && form.planned_end_on && form.planned_end_on < form.planned_start_on)
      return setLocalError('The planned end cannot be before the start.');
    if (form.load_to_recover.trim() && !/^\d+(\.\d+)?$/.test(form.load_to_recover.trim()))
      return setLocalError('Load to recover must be a number of 0 or more, or blank.');
    const body = jobWriteBody(form, original);
    if (original && Object.keys(body).length === 0) return onCancel();
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="wt-job-form">
      <WtFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WtField label="Well" hint={picked ? `Customer: ${picked.customerName ?? '—'}` : 'The customer is the well’s'}>
          <select className={WT_INPUT} aria-label="Well" value={form.well_id} onChange={(e) => set('well_id', e.target.value)}>
            <option value="">{wells.isLoading ? 'Loading…' : 'Choose…'}</option>
            {wellList.map((w) => (
              <option key={w.id} value={w.id}>
                {w.wellName}
                {wellIdOf(w, tenant) ? ` · ${wellIdOf(w, tenant)}` : ''}
                {w.customerName ? ` · ${w.customerName}` : ''}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Tickets">
          <select className={WT_INPUT} aria-label="Tickets" value={form.ticket_mode} onChange={(e) => set('ticket_mode', e.target.value)}>
            <option value="">Customer default</option>
            {(catalog?.ticketModes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Supervisor">
          <select className={WT_INPUT} aria-label="Supervisor" value={form.supervisor_id} onChange={(e) => set('supervisor_id', e.target.value)}>
            <option value="">None</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </WtField>
        {!job && (
          <WtField label="Start as">
            <select className={WT_INPUT} aria-label="Start as" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </WtField>
        )}
        <WtField label="Planned start">
          <input type="date" className={WT_DATE_INPUT} aria-label="Planned start" value={form.planned_start_on} onChange={(e) => set('planned_start_on', e.target.value)} />
        </WtField>
        <WtField label="Planned end">
          <input type="date" className={WT_DATE_INPUT} aria-label="Planned end" value={form.planned_end_on} onChange={(e) => set('planned_end_on', e.target.value)} />
        </WtField>
        <WtField label="Load to recover">
          <div className="flex gap-2">
            <input className={WT_INPUT} inputMode="decimal" aria-label="Load to recover" value={form.load_to_recover} onChange={(e) => set('load_to_recover', e.target.value)} />
            <select className={`${WT_INPUT} w-24 shrink-0`} aria-label="Load to recover unit" value={form.load_to_recover_unit} onChange={(e) => set('load_to_recover_unit', e.target.value)}>
              {liquidUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WtField>
        <WtField label="Flare permit">
          <input className={WT_INPUT} aria-label="Flare permit" value={form.flare_permit_ref} onChange={(e) => set('flare_permit_ref', e.target.value)} />
        </WtField>
        <WtField label="PO number">
          <input className={WT_INPUT} aria-label="PO number" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} />
        </WtField>
        <WtField label="AFE number">
          <input className={WT_INPUT} aria-label="AFE number" value={form.afe_number} onChange={(e) => set('afe_number', e.target.value)} />
        </WtField>
        <WtField label="Cost code">
          <input className={WT_INPUT} aria-label="Cost code" value={form.cost_code} onChange={(e) => set('cost_code', e.target.value)} />
        </WtField>
      </div>
      <WtField label="Scope">
        <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Scope" value={form.scope} onChange={(e) => set('scope', e.target.value)} />
      </WtField>
      <WtField label="Notes">
        <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WT_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
